import {
  ArrowCounterClockwise,
  ArrowRight,
  Check,
  Clipboard,
  PencilSimple,
  ShieldWarning,
  WarningCircle,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/Toast";
import { mockAssessment } from "../data/mock";
import { useAssessmentSession } from "../features/session/AssessmentSessionContext";
import { getAssessmentSnapshot } from "../services/api";
import type { AssessmentResult } from "../types/assessment";

export function ResultsPage() {
  const { id } = useParams();
  const { search } = useLocation();
  const { credentials } = useAssessmentSession();
  const [result, setResult] = useState<AssessmentResult>(() =>
    mockResult(new URLSearchParams(search).get("scenario")),
  );
  const [updated, setUpdated] = useState<string[]>([]);
  const toast = useToast();
  useEffect(() => {
    if (import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true" || !credentials)
      return;
    void getAssessmentSnapshot(credentials).then((snapshot) => {
      if (snapshot.result) setResult(snapshot.result);
    });
  }, [credentials]);
  const presentation = useMemo(
    () =>
      result.status === "passed"
        ? {
            label: "Passed",
            tone: "passed" as const,
            title: "Your application looks ready for final review.",
            icon: Check,
          }
        : result.status === "failed"
          ? {
              label: "High rejection risk",
              tone: "blocking" as const,
              title: "Important issues need attention before submission.",
              icon: ShieldWarning,
            }
          : {
              label: "Changes required",
              tone: "medium" as const,
              title: "A few updates can make this application stronger.",
              icon: WarningCircle,
            },
    [result.status],
  );
  const HeaderIcon = presentation.icon;
  return (
    <main className="mx-auto max-w-[1240px] px-5 py-8 sm:px-8 sm:py-10">
      {import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true" && (
        <div className="mb-6 rounded-2xl border border-brand/20 bg-brand/[.06] px-4 py-3 text-sm text-[#D8F59B]">
          <strong>Development Assessment Simulation.</strong> This result was
          not produced by a live AI or compliance assessment.
        </div>
      )}
      <Card className="overflow-hidden bg-[#111314]">
        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <div className="flex items-center gap-3">
              <span
                className={`grid h-11 w-11 place-items-center rounded-full ${result.status === "passed" ? "bg-brand/10 text-brand" : result.status === "failed" ? "bg-red-500/10 text-red-300" : "bg-amber/10 text-amber"}`}
              >
                <HeaderIcon size={24} />
              </span>
              <div>
                <p className="eyebrow">A2P readiness</p>
                <Badge tone={presentation.tone}>{presentation.label}</Badge>
              </div>
            </div>
            <h1 className="mt-6 max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">
              {presentation.title}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-[#A0A5A8]">
              Work from the highest-impact finding down. Re-run the assessment
              after updating the affected fields.
            </p>
          </div>
          <div className="min-w-44 rounded-[22px] border border-white/[.07] bg-[#171A1C] p-6 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-[#70777B]">
              Readiness score
            </p>
            <p className="mt-3 text-5xl font-semibold tracking-tight">
              {result.score}
              <span className="text-lg text-[#70777B]"> / 100</span>
            </p>
          </div>
        </div>
      </Card>
      <section className="mt-8">
        <div className="flex items-end justify-between">
          <div>
            <p className="eyebrow">Category review</p>
            <h2 className="mt-2 text-2xl font-semibold">
              What passed and what needs work
            </h2>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {result.categories.map((category) => (
            <Card key={category.id} className="min-h-36 p-5">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-semibold">{category.label}</h3>
                <Badge tone={category.severity}>
                  {category.severity === "passed" ? "Pass" : category.severity}
                </Badge>
              </div>
              <p className="mt-7 text-sm leading-6 text-[#70777B]">
                {category.summary}
              </p>
            </Card>
          ))}
        </div>
      </section>
      {result.issues.length > 0 && (
        <section className="mt-9">
          <p className="eyebrow">Recommended fixes</p>
          <h2 className="mt-2 text-2xl font-semibold">
            Resolve these before submission
          </h2>
          <div className="mt-5 space-y-4">
            {result.issues.map((issue) => (
              <Card
                key={issue.id}
                className={`p-5 sm:p-6 ${updated.includes(issue.id) ? "border-brand/30" : ""}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge tone={issue.severity}>{issue.severity}</Badge>
                      <span className="text-xs text-[#70777B]">
                        {issue.category}
                      </span>
                    </div>
                    <h3 className="mt-3 text-xl font-semibold">
                      {issue.title}
                    </h3>
                  </div>
                  {updated.includes(issue.id) && (
                    <span className="flex items-center gap-2 text-sm font-semibold text-brand">
                      <Check size={17} />
                      Marked updated
                    </span>
                  )}
                </div>
                <div className="mt-6 grid gap-5 lg:grid-cols-2">
                  <Detail
                    label="Current content"
                    value={issue.currentContent}
                  />
                  <Detail label="What was found" value={issue.problem} />
                </div>
                <div className="mt-5 rounded-2xl border border-brand/20 bg-brand/[.05] p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-brand">
                    Recommended replacement
                  </p>
                  <p className="mt-3 text-sm leading-7">
                    {issue.recommendedReplacement}
                  </p>
                  <Button
                    variant="secondary"
                    className="mt-4"
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(issue.recommendedReplacement)
                        .then(() =>
                          toast.show("Recommended replacement copied"),
                        )
                    }
                  >
                    <Clipboard size={16} />
                    Copy replacement
                  </Button>
                </div>
                <details className="mt-5 border-t border-white/[.07] pt-5">
                  <summary className="cursor-pointer text-sm font-semibold text-[#A0A5A8]">
                    Why this matters and source details
                  </summary>
                  <div className="mt-5 grid gap-5 lg:grid-cols-2">
                    <Detail label="Why it matters" value={issue.reason} />
                    <Detail
                      label="Possible rejection risk"
                      value={issue.possibleRejectionReason}
                    />
                    <Detail
                      label="Template used"
                      value={
                        issue.templateName
                          ? `${issue.templateName} · v${issue.templateVersion}`
                          : "No matching approved internal template was found."
                      }
                    />
                    <Detail
                      label="Documentation source"
                      value={issue.documentationSource}
                    />
                    <Detail
                      label="Confidence"
                      value={`${Math.round(issue.confidence * 100)}%`}
                    />
                  </div>
                </details>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    onClick={() =>
                      setUpdated((current) =>
                        current.includes(issue.id)
                          ? current
                          : [...current, issue.id],
                      )
                    }
                  >
                    <Check size={17} />
                    Mark as updated
                  </Button>
                  <Link to="/assessment/current">
                    <Button variant="secondary">
                      <PencilSimple size={17} />
                      Edit application
                    </Button>
                  </Link>
                  <Link to={`/assessment/${id}/processing`}>
                    <Button variant="ghost">
                      <ArrowCounterClockwise size={17} />
                      Re-run assessment
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}
      <Card className="mt-9 flex flex-wrap items-center justify-between gap-5 bg-[#171A1C] p-6">
        <div>
          <h2 className="text-lg font-semibold">
            {result.status === "passed"
              ? "Your submission pack is unlocked."
              : "Update the application, then assess it again."}
          </h2>
          <p className="mt-2 text-sm text-[#70777B]">
            {result.status === "passed"
              ? "Copy finalized answers or download generated files when the assessment Worker is connected."
              : "Previous results stay attached to this temporary session for comparison."}
          </p>
        </div>
        {result.status === "passed" ? (
          <Link to={`/assessment/${id}/submission-pack`}>
            <Button>
              Open submission pack <ArrowRight size={17} />
            </Button>
          </Link>
        ) : (
          <Link to="/assessment/current">
            <Button>
              Edit application <ArrowRight size={17} />
            </Button>
          </Link>
        )}
      </Card>
    </main>
  );
}
function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-[#70777B]">
        {label}
      </p>
      <p className="mt-2 text-sm leading-6 text-[#A0A5A8]">{value}</p>
    </div>
  );
}
function mockResult(scenario: string | null): AssessmentResult {
  if (scenario === "passed")
    return {
      ...mockAssessment,
      status: "passed",
      score: 96,
      issues: [],
      categories: mockAssessment.categories.map((category) => ({
        ...category,
        score: 96,
        severity: "passed",
        summary: "No material issue detected in this simulation.",
      })),
    };
  if (scenario === "high")
    return {
      ...mockAssessment,
      status: "failed",
      score: 48,
      issues: mockAssessment.issues.map((issue) => ({
        ...issue,
        severity: "blocking",
      })),
      categories: mockAssessment.categories.map((category, index) =>
        index < 4
          ? {
              ...category,
              severity: "high",
              score: 45,
              summary: "High-risk issue detected in this simulation.",
            }
          : category,
      ),
    };
  return mockAssessment;
}
